import { initNFTFallEffect } from './snowEffect.js';
import { initializeDarkModeToggle } from './darkMode.js';
import { imageFiles } from './pageConfig.js';

// pageWithEdit.js
export class EditSidebar {
  constructor(router) {
    console.log('EditSidebar constructor starting with router:', router);
    this.router = router;
    this.init();
    this.contentManager = new ContentBlockManager();
    this.inlineEditor = new InlineContentEditor(this);
    // Theme is initialized by the router per-view (page metadata vs dashboard preference).
    this.nftCache = new Map(); // Cache for NFT data per user address
    this.imageLoadPromises = new Map(); // Cache for image load validation
    this.eventListenersAttached = new Set(); // Track which dropdowns have event listeners
  }


  // Check if we have cached data for this user
  hasCache(userAddress) {
    return this.nftCache.has(userAddress);
  }

  // Get cached NFT data
  getCache(userAddress) {
    return this.nftCache.get(userAddress);
  }

  // Set cache for user address
  setCache(userAddress, validNFTs) {
    this.nftCache.set(userAddress, {
      nfts: validNFTs,
      timestamp: Date.now()
    });
  }

  // Check if cache is still valid (e.g., within 5 minutes)
  isCacheValid(userAddress, maxAge = 5 * 60 * 1000) {
    const cache = this.getCache(userAddress);
    if (!cache) return false;
    return (Date.now() - cache.timestamp) < maxAge;
  }

  async checkImageLoads(imageUrl) {
    // Check cache first
    if (this.imageLoadPromises.has(imageUrl)) {
      return this.imageLoadPromises.get(imageUrl);
    }

    const promise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = imageUrl;

      // Timeout after 5 seconds
      setTimeout(() => resolve(false), 5000);
    });

    this.imageLoadPromises.set(imageUrl, promise);
    return promise;
  }
  initializeSocialLinks(pageData) {
    const socialLinksToggle = document.getElementById('socialLinksEnabled');
    const socialLinksFields = document.getElementById('socialLinksFields');

    // Access social links data from the correct path
    const socialLinksData = pageData.metadata?.properties?.meta?.content?.socialLinks;
    const socialLinks = socialLinksData?.links || {};
    const hasSocialLinks = Object.values(socialLinks).some(link => link);

    if (socialLinksToggle && socialLinksFields) {
      // Remove any existing event listeners to prevent duplicates
      if (this.socialLinksToggleHandler) {
        socialLinksToggle.removeEventListener('change', this.socialLinksToggleHandler);
      }

      // Set toggle state based on existing data
      socialLinksToggle.checked = socialLinksData?.enabled || hasSocialLinks;

      // Show/hide fields based on toggle state
      socialLinksFields.style.display = socialLinksToggle.checked ? 'block' : 'none';

      console.log('Social links toggle state:', socialLinksToggle.checked);
      console.log('Social links fields display:', socialLinksFields.style.display);

      // Set existing values if they exist
      if (socialLinks) {
        const xInput = document.getElementById('xUrl');
        const discordInput = document.getElementById('discordUrl');
        const websiteInput = document.getElementById('websiteUrl');

        if (xInput && socialLinks.x) xInput.value = socialLinks.x;
        if (discordInput && socialLinks.discord) discordInput.value = socialLinks.discord;
        if (websiteInput && socialLinks.website) websiteInput.value = socialLinks.website;
      }

      // Create and store event handler
      this.socialLinksToggleHandler = (e) => {
        const isChecked = e.target.checked;
        socialLinksFields.style.display = isChecked ? 'flex' : 'none';
        socialLinksFields.classList.remove('hidden'); // Remove any hidden class

        console.log('Toggle changed to:', isChecked);
        console.log('Fields display now:', socialLinksFields.style.display);

        // Update the data model
        if (!pageData.metadata) pageData.metadata = {};
        if (!pageData.metadata.properties) pageData.metadata.properties = {};
        if (!pageData.metadata.properties.meta) pageData.metadata.properties.meta = {};
        if (!pageData.metadata.properties.meta.content) pageData.metadata.properties.meta.content = {};
        if (!pageData.metadata.properties.meta.content.socialLinks) {
          pageData.metadata.properties.meta.content.socialLinks = {
            enabled: isChecked,
            links: {
              x: document.getElementById('xUrl')?.value || '',
              website: document.getElementById('websiteUrl')?.value || '',
              discord: document.getElementById('discordUrl')?.value || ''
            }
          };
        } else {
          pageData.metadata.properties.meta.content.socialLinks.enabled = isChecked;
          // Update the links with current values
          pageData.metadata.properties.meta.content.socialLinks.links = {
            x: document.getElementById('xUrl')?.value || '',
            website: document.getElementById('websiteUrl')?.value || '',
            discord: document.getElementById('discordUrl')?.value || ''
          };
        }

        // Update the rendered social-links-section in real-time (if it exists)
        this.updateSocialLinksSection(pageData);
      };

      // Add toggle event listener
      socialLinksToggle.addEventListener('change', this.socialLinksToggleHandler);

      if (socialLinksFields) {
        console.log('Forcing display to block for testing');
        socialLinksFields.style.display = 'flex';
        socialLinksFields.classList.remove('hidden');
      }

      const updateSocialLinkValues = () => {
        if (pageData.metadata?.properties?.meta?.content?.socialLinks) {
          pageData.metadata.properties.meta.content.socialLinks.links = {
            x: document.getElementById('xUrl')?.value || '',
            website: document.getElementById('websiteUrl')?.value || '',
            discord: document.getElementById('discordUrl')?.value || ''
          };
          // Update the rendered section
          this.updateSocialLinksSection(pageData);
        }
      };

      const xInput = document.getElementById('xUrl');
      const discordInput = document.getElementById('discordUrl');
      const websiteInput = document.getElementById('websiteUrl');

      if (xInput) xInput.addEventListener('input', updateSocialLinkValues);
      if (discordInput) discordInput.addEventListener('input', updateSocialLinkValues);
      if (websiteInput) websiteInput.addEventListener('input', updateSocialLinkValues);

    } else {
      console.log('Social links elements not found in the DOM');
    }
  }

  updateSocialLinksSection(pageData) {
    // Find the social-links-section in the rendered page
    const socialLinksSection = document.querySelector('.social-links-section');
    const socialLinksData = pageData.metadata?.properties?.meta?.content?.socialLinks;

    // If the section doesn't exist but should be shown, create it
    if (!socialLinksSection && socialLinksData?.enabled) {
      const pageDescription = document.querySelector('.page-description');
      if (pageDescription) {
        const newSection = document.createElement('div');
        newSection.className = 'social-links-section';
        pageDescription.parentNode.insertBefore(newSection, pageDescription);
        this.populateSocialLinks(newSection, socialLinksData);
      }
    }
    // If the section exists and should be shown, update it
    else if (socialLinksSection && socialLinksData?.enabled) {
      this.populateSocialLinks(socialLinksSection, socialLinksData);
    }
    // If the section exists but should be hidden, remove it
    else if (socialLinksSection && !socialLinksData?.enabled) {
      socialLinksSection.remove();
    }
  }

  populateSocialLinks(container, socialLinksData) {
    // Clear existing content
    container.innerHTML = '';

    // Add social links icons based on data
    const links = socialLinksData.links || {};

    Object.entries(links)
      .filter(([_, url]) => url)
      .forEach(([platform, url]) => {
        const icon = {
          x: 'fa-brands fa-x-twitter',
          discord: 'fa-brands fa-discord',
          website: 'fa-solid fa-globe'
        }[platform];

        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.className = 'social-link';

        const iconElement = document.createElement('i');
        iconElement.className = icon;

        link.appendChild(iconElement);
        container.appendChild(link);
      });
  }

  applyDarkMode(isDark) {
    const body = document.body;
    const nameDisplay = document.getElementById('bnsv2NameDisplay');
    const pageDescription = document.querySelector('.page-description');


    body.style.backgroundColor = '#131313';
    if (nameDisplay) nameDisplay.style.color = '#F5F5F5';
    if (pageDescription) pageDescription.style.color = '#7d7d7d';

  }

  initializeProfilePhotoToggle(pageData) {
    const profilePhotoEnabled = document.getElementById('profilePhotoEnabled');
    const nftDropdown2 = document.getElementById('nftDropdownContainer2');
    const profileSection = document.querySelector('.profile-section');

    if (!profilePhotoEnabled || !nftDropdown2) return;

    // Check if profile image exists in the page data
    const hasProfileImage = pageData.metadata?.image || false;

    // Set the toggle state based on whether profile image exists
    profilePhotoEnabled.checked = hasProfileImage;

    // Show/hide the dropdown based on toggle state
    nftDropdown2.style.display = profilePhotoEnabled.checked ? 'block' : 'none';

    // Show/hide profile section based on toggle state
    profileSection.style.display = profilePhotoEnabled.checked ? 'flex' : 'none';

    // Setup NFT select event listener - moved outside the change handler
    const setupNFTSelectListener = () => {
      const select = nftDropdown2.querySelector('.nft-select');
      // Remove any existing listeners to avoid duplicates
      const newSelect = select.cloneNode(true);
      select.parentNode.replaceChild(newSelect, select);

      newSelect.addEventListener('change', (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex];
        const imageUrl = selectedOption.dataset.imageUrl;

        if (imageUrl) {
          const profileImage = document.querySelector('.profile-img');
          const profilePhoto = document.getElementById('profilePhoto');
          if (profilePhoto) {
            profilePhoto.style.backgroundImage = `url("${imageUrl}")`;
            profileImage.src = imageUrl;
          }
        }
      });
    };

    // If toggle is on, load NFTs and setup listeners
    if (profilePhotoEnabled.checked) {
      this.loadNFTs().then(() => {
        // Set up the NFT select listener
        setupNFTSelectListener();

        // If there's an image in the metadata, select the corresponding NFT in the dropdown
        if (hasProfileImage) {
          const select = nftDropdown2.querySelector('.nft-select');
          const imageUrl = pageData.metadata.image;

          // Convert IPFS URL to HTTP URL if needed for comparison
          const httpImageUrl = this.convertIpfsToHttps ?
            this.convertIpfsToHttps(imageUrl) : imageUrl;

          // Try to find and select the matching option
          if (select) {
            for (let i = 0; i < select.options.length; i++) {
              const option = select.options[i];
              if (option.dataset.imageUrl === httpImageUrl ||
                option.dataset.ipfsUrl === imageUrl) {
                select.selectedIndex = i;
                // Trigger change event to update the UI
                const event = new Event('change');
                select.dispatchEvent(event);
                break;
              }
            }
          }
        }
      });
    }

    // Add event listener for toggle changes
    profilePhotoEnabled.addEventListener('change', async (e) => {
      nftDropdown2.style.display = e.target.checked ? 'block' : 'none';

      if (e.target.checked) {
        profileSection.style.display = 'flex';
        await this.loadNFTs();
        setupNFTSelectListener(); // Reuse the same setup function
      } else {
        profileSection.style.display = 'none';
      }
    });
  }

  async loadNFTs() {
    const nftDropdown = document.getElementById('nftDropdownContainer');
    const nftDropdown2 = document.getElementById('nftDropdownContainer2');

    const festiveNFTData = this.currentPageData?.metadata?.attributes?.find(
      attr => attr.trait_type === 'Festive NFT'
    );

    const profileNFTData = this.currentPageData?.metadata?.attributes?.find(
      attr => attr.trait_type === 'Profile NFT'
    );

    await Promise.all([
      this.populateNFTOptions(nftDropdown, festiveNFTData),
      this.populateNFTOptions(nftDropdown2, profileNFTData)
    ]);
  }

  // initializeFeaturedImageToggle() {
  //   document.getElementById('featuredImageEnabled').addEventListener('change', function () {
  //     const imageUploadContainer = document.getElementById('imageUploadContainer');
  //     const featuredImageBlock = document.getElementById('featuredImageBlock');

  //     if (this.checked) {
  //       imageUploadContainer.classList.remove('hidden');
  //       featuredImageBlock.style.display = 'block';
  //     } else {
  //       imageUploadContainer.classList.add('hidden');
  //       featuredImageBlock.style.display = 'none';
  //     }
  //   });
  // }

  async getUserStxAddress() {
    // Simply return the address since we know authService exists
    return this.router.authService.getCurrentAddress();
  }

  async updateVisibility(matchingPage, cleanAddress) {
    const userAddress = await this.getUserStxAddress();
    if (userAddress && userAddress.toLowerCase() === cleanAddress.toLowerCase()) {
      this.setPageData(matchingPage);
      this.showEditButton(true);
    } else {
      this.showEditButton(false);
    }
  }

  getBaseStyles() {
    return `
      .edit-sidebar {
        position: fixed;
        top: 0;
        left: -350px;
        width: 350px;
        height: 100vh;
        background: white;
        box-shadow: 2px 0 8px rgba(0,0,0,0.1);
        transition: left 0.3s ease;
        z-index: 1000;
      }

      .edit-sidebar.visible {
        left: 0;
      }

      .edit-sidebar-content {
        padding: 20px;
      }

      .form-group {
        margin-bottom: 20px;
      }

      .form-group label {
        margin-bottom: 8px;
        font-weight: 500;
        display: flex;
      }

      .form-group input,
      .form-group textarea {
        width: 100%;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
      }

      .form-group textarea {
        min-height: 100px;
      }

      .save-btn {
        color: white;
        padding: 12px 18px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        width: 100%;
      }

      .save-btn:hover {
      padding: 12px 18px;
      border: none;
      background: #2b2a31;
      color: white;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      gap: 5px;
      }

      .edit-button {
        position: fixed;
        bottom: 20px;
        right: 20px;
        color: white;
        padding: 12px 24px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        display: none;
        z-index: 999;
      }

  


.nft-collection-fields {
    border: 1px solid #ddd;
    padding: 15px;
    border-radius: 4px;
    margin-top: 10px;
}

.upload-preview {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 10px;
}

.upload-preview img {
    width: 100%;
    height: 100px;
    object-fit: cover;
    border-radius: 4px;
}

.nft-upload {
    width: 100%;
    padding: 10px;
    border: 2px dashed #ddd;
    border-radius: 4px;
    margin-top: 5px; 
}
    `;
  }

  init() {
    // Create sidebar HTML with expanded form
    const sidebarHTML = `
    <div id="editSidebar" class="edit-sidebar">
        <div class="edit-sidebar-content">
        <div class="close-btn-container">
                <h2>Edit Page</h2>
              <button class="close-btn">&times;</button>
          </div>

            <form id="editPageForm">

                <div class="form-group">
                    <label for="pageTitle">Title</label>
                    <input type="text" id="pageTitle" name="title" required>
                </div>

                    <div class="form-group">
                    <label class="status-toggle">
                        <span>Profile Photo</span>
                        <input type="checkbox" id="profilePhotoEnabled" name="profilePhotoEnabled">
                        <span class="toggle-slider"></span>
                    </label>
            </div>

            <div id="nftDropdownContainer2" class="form-group nft-dropdown-container2" style="display: block;">
                <label for="nftSelect">Select NFT</label>
                <select id="profileNftSelect" class="nft-select">
                    <option value="">Select an NFT</option>
                </select>
                <div id="nftPreview2" class="nft-preview2"></div>
              </div>
                
                <div class="form-group">
                    <label class="status-toggle">
                        <span>Social Links</span>
                        
                        <input type="checkbox" id="socialLinksEnabled" name="socialLinksEnabled">


                        <span class="toggle-slider"></span>
                    </label>
                </div>

                <div class="form-group">

                <div id="socialLinksFields" class="form-group social-fields hidden">
                    
                    <div class="social-input">
                        <label for="xUrl">X</label>
                        <input type="url" id="xUrl" name="xUrl" 
                              placeholder="https://x.com/username"
                              class="social-url-input">
                    </div>
                    
                    <div class="social-input">
                        <label for="websiteUrl">Website</label>
                        <input type="url" id="websiteUrl" name="websiteUrl" 
                              placeholder="https://example.com"
                              class="social-url-input">
                    </div>
                    
                    <div class="social-input">
                        <label for="discordUrl">Discord</label>
                        <input type="url" id="discordUrl" name="discordUrl" 
                              placeholder="https://discord.com/username"
                              class="social-url-input">
                    </div>
                
                </div>
                
                </div>

                <div class="form-group">
                  <label for="pageDescription">Description</label>
                  <textarea id="pageDescription" name="description" required></textarea>
                </div>

                <div class="form-group">
                  <label class="status-toggle">
                      <span>Show Ownership</span>
                      <input type="checkbox" id="ownershipEnabled" name="ownershipEnabled" checked>
                      <span class="toggle-slider"></span>
                  </label>
              </div>

          <div class="form-group content-blocks-section">
        <h3>Content Blocks</h3>
        <div class="content-blocks">
        
          <div class="content-block" draggable="true" data-type="heading">
            <div class="block-preview">
              <span class="block-icon">H</span>
              <span class="block-label">Heading</span>
            </div>
          </div>

        <div class="content-block" draggable="true" data-type="paragraph">
            <div class="block-preview">
              <span class="block-icon">¶</span>
              <span class="block-label">Paragraph</span>
            </div>
        </div>

        <!-- Image block option -->
        <div class="content-block" draggable="true" data-type="image">
            <div class="block-preview">
              <span class="block-icon">🖼️</span>
              <span class="block-label">Image</span>
            </div>
        </div>

        </div>
        <div id="pageContent">

          <div class="blocks-container"></div>
        </div>
      </div>

                <div class="form-group">
                    <label class="status-toggle">
                        <span>Dark Mode</span>
                        <input type="checkbox" id="darkModeEnabled" name="darkModeEnabled">
                        <span class="toggle-slider"></span>
                    </label>
                </div>
        

          <div class="form-group">
            <div id="nftCollectionFields" class="form-group nft-collection-fields hidden">
                
            <div class="collection-input">
              <label for="collectionName">Collection Name</label>
              <input type="text" id="collectionName" name="collectionName" placeholder="My NFT Collection">
            </div>

            <div class="collection-input">
                <label for="nftUpload">Upload GIFs/Images (Max 12)</label>
                <input type="file" id="nftUpload" name="nftUpload" multiple accept="image/gif,image/png,image/jpeg" class="nft-upload">
                <div id="uploadPreview" class="upload-preview grid"></div>
            </div>

            </div>  
          </div>

          <div class="form-group">
              <label class="festive-toggle">
                  <span>Festive Mode</span>
                  <input type="checkbox" id="festiveMode" name="festive">
                  <span class="toggle-slider"></span>
              </label>
          </div>

          <div id="nftDropdownContainer" class="form-group nft-dropdown-container" style="display: none;">
              <label for="nftSelect">Select NFT</label>
              <select id="nftSelect" class="nft-select">
                  <option value="">Select an NFT</option>
              </select>
              <div id="nftPreview" class="nft-preview2"></div>
          </div>

          <div class="form-group">
                    <label class="status-toggle">
                        <span>Active Status</span>
                        <input type="checkbox" id="activeStatus" name="active">
                        <span class="toggle-slider"></span>
                    </label>
                </div>

                           <div class="form-group">
            <label class="status-toggle">
                <span>BNS Name</span>
                <input type="checkbox" id="bnsEnabled" name="bnsEnabled">
                <span class="toggle-slider"></span>
            </label>
        </div>

        <div id="bnsFields" class="form-group bns-fields" style="display: none;">
            <div class="bns-dropdown-container">
                <label for="bnsDropdown">Choose your BNS name</label>
                <select id="bnsDropdown" class="bns-select">
                    <option value="">Select a BNS name</option>
                </select>
                <div id="bnsLoadingMessage" class="bns-loading" style="display: none;">
                    Loading your BNS names...
                </div>
            </div>
            
            <div id="urlPreview" class="url-preview" style="display: none;">
                <!-- URL preview will be populated by JavaScript -->
            </div>
            
            <div class="bns-help">
                <p class="help-text">
                    <i class="fa-solid fa-info-circle"></i>
                    Don't have a BNS name? 
                    <a href="https://bns.one/" target="_blank">Register one here</a>
                </p>
            </div>
        </div>

                <button type="submit" class="save-btn">Update Page</button>
            </form>
  </div>
  </div>
`;
    // Add additional styles
    const styles = `
            ${this.getBaseStyles()}
            
            /* Toggle Styles */
              .blockchain-warning {
        display: none;
        background-color: #fff3cd;
        color: #856404;
        border: 1px solid #ffeeba;
        border-radius: 4px;
        padding: 10px 15px;
        margin: 10px 0;
        font-size: 14px;
        font-weight: bold;
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        align-items: center;
        text-align: center;
        
      }
          
          .blockchain-warning::before {
            content: "⚠️";
            margin-right: 8px;
          }
            .status-toggle, .festive-toggle {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                padding: 10px 0;
            }

            .toggle-slider {
                position: relative;
                display: inline-block;
                width: 50px;
                height: 24px;
                background: #ccc;
                border-radius: 12px;
                cursor: pointer;
                transition: 0.3s;
            }

            .toggle-slider:before {
                content: '';
                position: absolute;
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: white;
                top: 2px;
                left: 2px;
                transition: 0.3s;
            }

            input[type="checkbox"] {
                display: none;
            }

            input[type="checkbox"]:checked + .toggle-slider {
                background: #4CAF50;
            }

            input[type="checkbox"]:checked + .toggle-slider:before {
                transform: translateX(26px);
            }

            /* NFT Dropdown Styles */
            .nft-dropdown-container {
                margin-top: 15px;
            }

            .nft-dropdown-container2 {
                margin-top: 15px;
            }

            .nft-collection-toggle{
              display: flex;
              flex-direction: row;
              justify-content: space-between;
            }

              .dark-mode-toggle{
              display: flex;
              flex-direction: row;
              justify-content: space-between;
            }
            .nft-select {
                width: 100%;
                padding: 8px;
                border: 1px solid #ddd;
                border-radius: 4px;
                margin-bottom: 10px;
            }

            .nft-preview2 {
              display: none;  /* Hide by default */
              align-items: center;
              gap: 10px;
                  }

                     /* Only show when it has content */
            .nft-preview2:not(:empty) {
                display: flex;
            }

            .nft-preview2 img {
                border-radius: 4px;
                object-fit: cover;
            }
        `;

    // Add styles to document
    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);

    // Add sidebar to document
    document.body.insertAdjacentHTML('beforeend', sidebarHTML);

    // Add edit button
    const editButton = document.createElement('button');
    editButton.className = 'edit-button';
    editButton.textContent = 'Edit Page';
    document.body.appendChild(editButton);

    // Create and add blockchain warning to the main page
    const blockchainWarning = document.createElement('div');
    blockchainWarning.id = 'blockchainStatus';
    blockchainWarning.className = 'blockchain-warning';
    blockchainWarning.textContent = 'Changes not yet saved to blockchain';

    // Insert the warning after the edit button
    document.body.appendChild(blockchainWarning);

    // Use more visible fixed positioning
    blockchainWarning.style.position = 'fixed';
    blockchainWarning.style.top = '10px';  // Position at top of viewport
    blockchainWarning.style.left = '50%';  // Center horizontally
    blockchainWarning.style.transform = 'translateX(-50%)';  // Center adjustment
    blockchainWarning.style.zIndex = '9999';  // Ensure it's above other elements


    this.blockchainWarning = blockchainWarning;


    // Store elements
    this.sidebar = document.getElementById('editSidebar');
    this.editButton = editButton;
    this.form = document.getElementById('editPageForm');
    this.blockchainWarning = blockchainWarning;

    console.log('Edit button in DOM after init:', document.querySelector('.edit-button'));

    // Add event listeners
    this.addEventListeners();

  }

  addEventListeners() {
    this.isSidebarVisible = false;

    // Close button
    this.sidebar.querySelector('.close-btn').addEventListener('click', () => {
      this.hide();
    });

    // Edit button - Toggle sidebar visibility
    this.editButton.addEventListener('click', () => {
      // Check if sidebar has the 'visible' class
      if (this.sidebar.classList.contains('visible')) {
        this.hide();
      } else {
        this.show();
      }
    });


    // Show the blockchain warning whenever the user makes changes
    this.form.addEventListener('input', () => {
      console.log('Input detected, showing warning');
      this.blockchainWarning.style.display = 'flex';
      console.log('Blockchain warning created:', this.blockchainWarning);


    });


    const titleInput = document.getElementById('pageTitle');
    const pageTitle = document.getElementById('bnsv2NameDisplay');
    // Adjust selector to match your page title element
    const descriptionInput = document.getElementById('pageDescription');
    const pageDescription = document.querySelector('.page-description');


    // Social links toggle
    const socialLinksToggle = document.getElementById('socialLinksEnabled');
    const socialLinksFields = document.getElementById('socialLinksFields');
    socialLinksToggle?.addEventListener('change', (e) => {
      if (socialLinksFields) {
        socialLinksFields.style.display = e.target.checked ? 'block' : 'none';
        console.log('Social links display updated to:', socialLinksFields.style.display);
      }
      this.blockchainWarning.style.display = 'flex';

    });

    // Update page title when form title changes
    titleInput?.addEventListener('input', (e) => {
      if (pageTitle) {
        pageTitle.textContent = e.target.value;
      }
    });

    descriptionInput?.addEventListener('input', (e) => {
      if (pageDescription) {
        pageDescription.textContent = e.target.value;
      }
    });

    const ownershipToggle = document.getElementById('ownershipEnabled');
    if (ownershipToggle) {
      ownershipToggle.addEventListener('change', (event) => {
        // This will show/hide the ownership section in real-time in the editor
        // You can toggle a preview class or make other UI updates here
        console.log('Ownership toggle changed:', event.target.checked);

        // Optional: To see the effect immediately without saving,
        // you could temporarily hide/show a preview of the ownership section
        const previewSection = document.querySelector('.ownership-section');
        if (previewSection) {
          previewSection.style.display = event.target.checked ? 'block' : 'none';
        }

        // Mark as unsaved changes
        this.blockchainWarning.style.display = 'flex';

      });
    }
    // Festive mode toggle
    const festiveToggle = document.getElementById('festiveMode');
    const nftDropdown = document.getElementById('nftDropdownContainer');
    festiveToggle?.addEventListener('change', (e) => {
      nftDropdown.style.display = e.target.checked ? 'block' : 'none';

      if (e.target.checked) {
        this.loadNFTs();
      } else {
        // Clean up NFT fall effect when toggling off
        if (window.currentNFTFallEffect) {
          try {
            document.body.removeChild(window.currentNFTFallEffect.canvas);
            window.currentNFTFallEffect = null;
            console.log('NFT Fall Effect cleaned up successfully');
          } catch (error) {
            console.error('Error cleaning up NFT Fall Effect:', error);
          }
        }
      }
      this.blockchainWarning.style.display = 'flex';

    });


    const nftCollectionToggle = document.getElementById('nftCollectionEnabled');
    const nftCollectionFields = document.getElementById('nftCollectionFields');
    const nftUpload = document.getElementById('nftUpload');
    const uploadPreview = document.getElementById('uploadPreview');

    nftCollectionToggle?.addEventListener('change', (e) => {
      nftCollectionFields.classList.toggle('hidden', !e.target.checked);
    });

    nftUpload?.addEventListener('change', async (e) => {
      uploadPreview.innerHTML = '';
      const files = Array.from(e.target.files).slice(0, 12);

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          const pinataApiUrl = 'https://api.pinata.cloud/pinning/pinFileToIPFS';  // Changed endpoint
          const pinataApiKey = process.env.PINATA_API_KEY;
          const pinataSecretApiKey = process.env.PINATA_SECRET_KEY;

          const ipfsResponse = await fetch(pinataApiUrl, {
            method: 'POST',
            headers: {
              'pinata_api_key': pinataApiKey,
              'pinata_secret_api_key': pinataSecretApiKey
            },
            body: formData
          });

          const { IpfsHash } = await ipfsResponse.json();
          const ipfsUrl = `ipfs://${IpfsHash}`;

          const container = document.createElement('div');
          container.className = 'nft-preview-item';
          container.innerHTML = `
                <img src="https://ipfs.io/ipfs/${IpfsHash}" alt="NFT Preview">
                <input type="text" 
                    class="nft-name-input" 
                    data-ipfs="${ipfsUrl}"
                    placeholder="NFT Name"
                    value="${file.name.split('.')[0]}">
            `;
          uploadPreview.appendChild(container);
        } catch (error) {
          console.error('Error uploading to IPFS:', error);
        }
      }
    });

    // Form submission

    // Form submission event handler
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(this.form);

      try {
        // Collect form data
        const pageId = this.currentPageId;
        const title = formData.get('title') || '';
        const description = formData.get('description') || '';
        const slug = this.router.sanitizeUrl(title);
        const stxAddress = this.router.currentStxAddress || '';
        const active = document.getElementById('activeStatus')?.checked || false;
        const contractType = 'gated-pages';

        // Collect social links data
        const socialLinksEnabled = document.getElementById('socialLinksEnabled')?.checked || false;
        const socialLinksData = {
          enabled: socialLinksEnabled,
          links: {
            x: document.getElementById('xUrl')?.value || '',
            discord: document.getElementById('discordUrl')?.value || '',
            website: document.getElementById('websiteUrl')?.value || ''
          }
        };

        // Get festive mode setting
        const festiveMode = document.getElementById('festiveMode');
        const isFestiveModeEnabled = festiveMode?.checked || false;

        // Collect content blocks in a consistent format
        const contentBlocks = this.collectContentBlocks();

        // Get NFT collection data if enabled
        const nftCollectionData = this.collectNFTCollectionData();

        // Get selected NFT for festive mode
        const selectedNFTData = this.collectSelectedNFTData(isFestiveModeEnabled);

        // Show confirmation modal
        await this.router.showUpdateConfirmation(
          pageId,
          title,
          description,
          slug,
          stxAddress,
          active,
          contractType,
          socialLinksData,
          contentBlocks,
          nftCollectionData,
          selectedNFTData
        );

        const blockchainStatus = document.getElementById('blockchainStatus');
        if (blockchainStatus) blockchainStatus.style.display = 'none';

        this.hide();
        this.blockchainWarning.style.display = 'none';

      } catch (error) {
        console.error('Error in update flow:', error);
        alert('Failed to update page. Please try again: ' + error.message);
      }
    });

  }

  collectContentBlocks() {
    return Array.from(document.querySelectorAll('.editable-block')).map(block => {
      const editable = block.querySelector('.editable');
      const blockData = {
        type: block.dataset.type || 'paragraph',
        order: Array.from(block.parentNode.children).indexOf(block),
        content: editable ? editable.textContent || '' : '',
        innerHTML: editable ? editable.innerHTML
          .split('<br>')
          .join('\n')
          .replace(/<div>/g, '\n')
          .replace(/<\/div>/g, '')
          .trim() : ''
      };

      // For paragraphs with links, extract and store URLs
      if (block.dataset.type === 'paragraph' && block.id && this.extractUrl) {
        const urls = this.extractUrl(blockData.content);
        if (urls && urls.length > 0) {
          blockData.urls = urls;

          // Find link preview for this block
          const linkPreview = document.querySelector(`.external-link-preview[data-for="${block.id}"]`);
          if (linkPreview && linkPreview.style.display !== 'none') {
            blockData.hasLinkPreview = true;
          }
        }
      }


      return blockData;
    });
  }

  collectNFTCollectionData() {
    const nftCollectionEnabled = document.getElementById('nftCollectionEnabled')?.checked;
    if (!nftCollectionEnabled) return null;

    const nfts = Array.from(document.querySelectorAll('.nft-preview-item')).map(item => ({
      name: item.querySelector('.nft-name-input').value,
      image: item.querySelector('img').src
    }));

    return {
      enabled: true,
      name: document.getElementById('collectionName').value,
      items: nfts
    };
  }

  collectSelectedNFTData(isFestiveModeEnabled) {
    if (!isFestiveModeEnabled) return null;

    const originalSelect = document.getElementById('nftSelect');
    if (!originalSelect || originalSelect.selectedIndex <= 0) return null;

    const selectedOption = originalSelect.options[originalSelect.selectedIndex];
    return {
      festiveNFT: selectedOption.value || '',
      selectedNFTName: selectedOption.textContent || '',
      selectedNFTImage: selectedOption.dataset.imageUrl || ''
    };
  }

  setPageData(pageData) {
    console.log('Setting page data:', pageData);
    this.currentPageId = pageData.id;
    this.currentPageData = pageData;

    // Set basic form values
    this.form.title.value = pageData.title;
    this.form.description.value = pageData.description;

    // Update the page title display
    const pageTitle = document.getElementById('bnsv2NameDisplay');
    if (pageTitle) {
      pageTitle.textContent = pageData.title;
    }

    const pageDescription = document.querySelector('page-description');
    if (pageDescription) {
      pageDescription.textContent = pageData.pageDescription;
    }

    this.initializeProfilePhotoToggle(pageData);
    // Initialize social links
    this.initializeSocialLinks(pageData);
    this.initializeBNSToggle(pageData);

    // Rest of your existing code for active status and festive mode...
    const activeToggle = document.getElementById('activeStatus');
    if (activeToggle) {
      if (pageData.value?.active?.value !== undefined) {
        activeToggle.checked = pageData.value.active.value === true;
      } else if (pageData.active?.value !== undefined) {
        activeToggle.checked = pageData.active.value === true;
      } else {
        activeToggle.checked = !!pageData.active;
      }
    }

    // Handle festive mode and NFT dropdown
    const festiveToggle = document.getElementById('festiveMode');
    const nftDropdown = document.getElementById('nftDropdownContainer');

    if (festiveToggle && pageData.metadata?.attributes) {
      const festiveMode = pageData.metadata.attributes.find(
        attr => attr.trait_type === 'Festive Mode'
      );
      festiveToggle.checked = festiveMode?.value === 'On';

      if (nftDropdown) {
        nftDropdown.style.display = festiveMode?.value === 'On' ? 'block' : 'none';
        if (festiveMode?.value === 'On') {
          this.loadNFTs();
        }
      }
    }

    const profilePhotoEnabled = document.getElementById('profilePhotoEnabled');
    if (profilePhotoEnabled) {
      const profileNFTData = pageData.metadata?.attributes?.find(
        attr => attr.trait_type === 'Profile NFT'
      );

      const nftDropdown2 = document.getElementById('nftDropdownContainer2');
      if (nftDropdown2) {
        // Show/hide dropdown based on toggle state
        nftDropdown2.style.display = profilePhotoEnabled.checked ? 'block' : 'none';

        // Load NFTs only if dropdown is visible
        if (profilePhotoEnabled.checked) {
          this.loadNFTs();
        }
      }
    }
  }

  show() {
    this.sidebar.classList.add('visible');
    this.isSidebarVisible = true;

  }

  hide() {
    this.sidebar.classList.remove('visible');
    this.isSidebarVisible = false;

  }

  showEditButton(show) {
    this.editButton.style.display = show ? 'block' : 'none';
  }

  cleanup() {
    // Remove event listeners
    this.sidebar.querySelector('.close-btn').removeEventListener('click', this.hide);
    this.editButton.removeEventListener('click', this.show);

    // Remove elements
    this.sidebar.remove();
    this.editButton.remove();
  }

  async loadNFTs() {
    const nftDropdown = document.getElementById('nftDropdownContainer');
    const nftDropdown2 = document.getElementById('nftDropdownContainer2');

    const festiveNFTData = this.currentPageData?.metadata?.attributes?.find(
      attr => attr.trait_type === 'Festive NFT'
    );

    const profileNFTData = this.currentPageData?.metadata?.attributes?.find(
      attr => attr.trait_type === 'Profile NFT'
    );

    await Promise.all([
      this.populateNFTOptions(nftDropdown, festiveNFTData),
      this.populateNFTOptions(nftDropdown2, profileNFTData)
    ]);
  }

  checkImageLoads(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  async populateNFTOptions(dropdownContainer, festiveNFTData) {
    const select = dropdownContainer.querySelector('.nft-select');
    if (!select) return;

    console.log("Step 6: Populating dropdown with NFTs");

    // Hide the select and show loading initially
    select.style.display = 'none';

    // Remove any existing loading message
    const existingLoadingMessage = dropdownContainer.querySelector('.loading-message');
    if (existingLoadingMessage) {
      existingLoadingMessage.remove();
    }

    const loadingMessage = document.createElement('div');
    loadingMessage.className = 'loading-message';
    loadingMessage.textContent = 'Loading NFTs...';
    dropdownContainer.insertBefore(loadingMessage, select);

    try {
      const userAddress = await this.getUserStxAddress();
      console.log('Got user address:', userAddress);

      let validNFTs = null;

      // Check if we have valid cached data
      if (this.isCacheValid(userAddress)) {
        console.log("Using cached NFT data");
        validNFTs = this.getCache(userAddress).nfts;
      } else {
        console.log("Fetching fresh NFT data");
        const nftData = await this.fetchNFTs(userAddress);
        console.log("Step 6.2: NFTs Found:", nftData);

        if (!nftData || !nftData.results) {
          throw new Error('No NFT data available');
        }

        // Process all NFTs
        validNFTs = await this.processNFTData(nftData.results);

        // Cache the results
        this.setCache(userAddress, validNFTs);
      }

      // Clear existing options properly
      this.clearSelectOptions(select);

      // Create or find preview div
      const previewDiv = this.getOrCreatePreviewDiv(dropdownContainer);

      // Only proceed if we found valid NFTs
      if (validNFTs.length > 0) {
        // Add all valid NFTs to select
        this.populateSelectOptions(select, validNFTs, festiveNFTData);

        // Remove loading message and show select
        loadingMessage.remove();
        select.style.display = 'block';

        // Setup change listener (only once per dropdown)
        this.setupChangeListener(dropdownContainer, select, previewDiv);
      } else {
        loadingMessage.textContent = 'No valid NFTs found';
      }

    } catch (error) {
      console.error('Error populating NFT options:', error);
      loadingMessage.textContent = 'Error loading NFTs';
      throw error;
    }
  }

  async processNFTData(nftResults) {
    const validNFTs = [];

    for (const nft of nftResults) {
      const nftIdentifier = nft.asset_identifier;
      const nftId = nft.value.repr.replace('u', "");
      const [contractAddress, assetName] = nftIdentifier.split('::');

      try {
        const metadataUrl = `/api/proxy/nft-metadata/${encodeURIComponent(contractAddress)}/${encodeURIComponent(nftId)}`;
        const response = await fetch(metadataUrl);

        if (!response.ok) continue;

        const metadata = await response.json();
        const imageUrl = metadata.metadata.cached_thumbnail_image;

        if (!imageUrl || !(await this.checkImageLoads(imageUrl))) {
          continue;
        }

        validNFTs.push({
          identifier: nftIdentifier,
          id: nftId,
          name: assetName,
          imageUrl: imageUrl
        });

      } catch (error) {
        console.log(`Skipping NFT ${assetName} #${nftId} - error occurred`);
        continue;
      }
    }

    return validNFTs;
  }

  clearSelectOptions(select) {
    // Save the default option and preserve any pre-selected option
    const defaultOption = select.querySelector('option[value=""]');
    const currentlySelected = select.querySelector('option[selected]');

    // Clear all options
    select.innerHTML = '';

    // Restore default option
    if (defaultOption) {
      select.appendChild(defaultOption.cloneNode(true));
    }

    // If there was a selected option that wasn't the default, restore it
    if (currentlySelected && currentlySelected !== defaultOption) {
      select.appendChild(currentlySelected.cloneNode(true));
    }
  }

  getOrCreatePreviewDiv(dropdownContainer) {
    let previewDiv = dropdownContainer.querySelector('.nft-preview2');

    if (!previewDiv) {
      previewDiv = document.createElement('div');
      previewDiv.className = 'nft-preview2';
      previewDiv.style.marginTop = '10px';
      previewDiv.style.display = 'none';
      dropdownContainer.appendChild(previewDiv);
    }

    return previewDiv;
  }

  populateSelectOptions(select, validNFTs, festiveNFTData) {
    validNFTs.forEach(nft => {
      const option = document.createElement('option');
      option.value = nft.identifier;
      option.textContent = `${nft.name} #${nft.id}`;
      option.dataset.imageUrl = nft.imageUrl;
      option.dataset.nftId = nft.id;

      if (nft.identifier === festiveNFTData?.identifier) {
        option.selected = true;
      }

      select.appendChild(option);
    });
  }

  setupChangeListener(dropdownContainer, select, previewDiv) {
    // Check if we've already attached a listener to this dropdown
    if (this.eventListenersAttached.has(dropdownContainer.id)) {
      return;
    }

    this.eventListenersAttached.add(dropdownContainer.id);

    select.addEventListener('change', async (e) => {
      const selectedOption = e.target.options[e.target.selectedIndex];
      const imageUrl = selectedOption.dataset.imageUrl;

      if (imageUrl) {
        previewDiv.innerHTML = `
          <img src="${imageUrl}" 
              alt="NFT Preview" 
              style="max-width: 100px; height: auto; border-radius: 4px;">
      `;
        previewDiv.style.display = 'block';

        // Only handle fall effect for festive mode dropdown
        if (dropdownContainer.id === 'nftDropdownContainer') {
          // Clean up previous fall effect
          if (window.currentNFTFallEffect) {
            const container = document.querySelector('.profile-section');
            if (container && container.contains(window.currentNFTFallEffect.canvas)) {
              container.removeChild(window.currentNFTFallEffect.canvas);
            }
            window.currentNFTFallEffect = null;
          }

          // Initialize new fall effect
          const container = document.querySelector('.profile-section');
          if (container) {
            window.currentNFTFallEffect = initNFTFallEffect(imageUrl);
          }
        }
      }
    });
  }

  async fetchNFTs(userAddress) {
    console.log('Fetching NFTs for address:', userAddress);
    const nftHoldingsUrl = `/api/proxy/stacks?principal=${encodeURIComponent(userAddress)}&limit=50`;
    console.log('Fetching from URL:', nftHoldingsUrl);

    try {
      const response = await fetch(nftHoldingsUrl);
      console.log('NFT API response status:', response.status);

      if (!response.ok) {
        throw new Error(`Failed to fetch NFT holdings: ${response.status}`);
      }

      const data = await response.json();
      console.log('NFT API response data:', data);
      return data;
    } catch (error) {
      console.error('Error fetching NFTs:', error);
      throw error;
    }
  }


  async updatePage(pageData) {
    try {
      const festiveToggle = document.getElementById('festiveMode');
      const profileToggle = document.getElementById('profilePhotoEnabled');
      const festiveSelect = document.getElementById('nftSelect');
      const profileSelect = document.getElementById('profileNftSelect');

      let nftData = {};
      if (festiveToggle?.checked && festiveSelect?.value) {
        const selectedOption = festiveSelect.options[festiveSelect.selectedIndex];
        nftData.festiveNFT = selectedOption.value;
        nftData.selectedNFTName = selectedOption.textContent;
        nftData.selectedNFTImage = selectedOption.dataset.imageUrl;
      }

      if (profileToggle?.checked && profileSelect?.value) {
        const selectedOption = profileSelect.options[profileSelect.selectedIndex];
        nftData.profileNFT = selectedOption.value;
        nftData.profileNFTName = selectedOption.textContent;
        nftData.profileNFTImage = selectedOption.dataset.imageUrl;
      }

      const result = await this.router.updatePage({
        id: this.currentPageId,
        title: pageData.title,
        description: pageData.description,
        active: document.getElementById('activeStatus').checked,
        contractType: 'gated-pages',
        slug: this.router.sanitizeUrl(pageData.title),
        festive: festiveToggle?.checked || false,
        ...nftData
      });

      if (result) {
        this.hide();
        if (window.currentNFTFallEffect) {
          document.body.removeChild(window.currentNFTFallEffect.canvas);
          window.currentNFTFallEffect = null;
        }
      }
    } catch (error) {
      console.error('Error updating page:', error);
      throw error;
    }
  }


  async initializeBNSToggle(pageData) {
  const bnsToggle = document.getElementById('bnsEnabled');
  const bnsFields = document.getElementById('bnsFields');
  const bnsDropdown = document.getElementById('bnsDropdown');

  if (!bnsToggle || !bnsFields) return;

  // Check if BNS name exists in the page data
  const currentBNSName = pageData.metadata?.properties?.meta?.content?.bnsName;
  const hasBNSName = currentBNSName && currentBNSName.length > 0;

  // Set the toggle state based on whether BNS name exists
  bnsToggle.checked = hasBNSName;

  // Show/hide the fields based on toggle state
  bnsFields.style.display = bnsToggle.checked ? 'block' : 'none';

  // Set existing BNS name if it exists
  if (hasBNSName && bnsDropdown) {
    // We'll populate this after loading owned BNS names
  }

  // Load owned BNS names when toggle is enabled
  if (bnsToggle.checked) {
    await this.loadOwnedBNSNames();
  }

  // Add event listener for toggle changes
  bnsToggle.addEventListener('change', async (e) => {
    const isChecked = e.target.checked;
    bnsFields.style.display = isChecked ? 'block' : 'none';

    if (isChecked) {
      await this.loadOwnedBNSNames();
    }

    // Update the data model
    if (!pageData.metadata) pageData.metadata = {};
    if (!pageData.metadata.properties) pageData.metadata.properties = {};
    if (!pageData.metadata.properties.meta) pageData.metadata.properties.meta = {};
    if (!pageData.metadata.properties.meta.content) pageData.metadata.properties.meta.content = {};
    
    pageData.metadata.properties.meta.content.bnsEnabled = isChecked;
    
    if (!isChecked) {
    // Clear BNS name when disabled - handle both data structures
    pageData.metadata.properties.meta.content.bnsName = '';
    
    // Also clear the bns object structure if it exists
    if (pageData.metadata.bns) {
      pageData.metadata.bns.bnsName = '';
    }
    
    // Clear the dropdown selection
    if (bnsDropdown) {
      bnsDropdown.value = '';
    }
}

    // Show blockchain warning
    this.blockchainWarning.style.display = 'flex';
  });

  // Add event listener for BNS selection changes
  if (bnsDropdown) {
    bnsDropdown.addEventListener('change', (e) => {
      const selectedBNS = e.target.value;
      
      // Update the data model
      if (pageData.metadata?.properties?.meta?.content) {
        pageData.metadata.properties.meta.content.bnsName = selectedBNS;
      }

      // Update the page URL preview
      this.updateURLPreview(selectedBNS);

      // Show blockchain warning
      this.blockchainWarning.style.display = 'flex';
    });
  }
}


updateURLPreview(bnsName) {
  console.log('updateURLPreview called with:', bnsName);
  
  const urlPreview = document.getElementById('urlPreview');
  console.log('urlPreview element found:', !!urlPreview);
  
  if (!urlPreview) {
    console.warn('urlPreview element not found in DOM');
    return;
  }
  
  try {
    if (bnsName && bnsName.trim()) {
      const cleanBnsName = bnsName.trim();
      const newUrl = `gated.so/#/${cleanBnsName}`;
      const fullUrl = `https://${newUrl}`;
      
      console.log('Creating URL preview for:', newUrl);
      
      // Create the HTML without inline onclick
      urlPreview.innerHTML = `
        <div class="url-preview-content">
          <span class="url-label">Your page will be available at:</span>
          <span class="url-value">${newUrl}</span>
          <button class="copy-url-btn" data-url="${fullUrl}">Copy</button>
        </div>
      `;
      
      // Add event listener to the copy button
      const copyButton = urlPreview.querySelector('.copy-url-btn');
      if (copyButton) {
        // Remove any existing event listeners
        copyButton.removeEventListener('click', this.handleCopyClick);
        
        // Add new event listener with proper context binding
        this.handleCopyClick = (e) => {
          e.preventDefault();
          const urlToCopy = e.target.dataset.url;
          this.copyToClipboard(urlToCopy);
        };
        
        copyButton.addEventListener('click', this.handleCopyClick);
      }
      
      urlPreview.style.display = 'block';
    } else {
      console.log('No BNS name provided, hiding preview');
      urlPreview.style.display = 'none';
      urlPreview.innerHTML = '';
    }
  } catch (error) {
    console.error('Error in updateURLPreview:', error);
    urlPreview.style.display = 'none';
  }
}


// Add this helper method to handle copying to clipboard safely
copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        console.log('URL copied to clipboard:', text);
        // Show a brief success message
        this.showCopySuccess();
      }).catch(err => {
        console.error('Failed to copy with clipboard API:', err);
        this.fallbackCopyToClipboard(text);
      });
    } else {
      this.fallbackCopyToClipboard(text);
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
  }
}

// Fallback copy method for older browsers
fallbackCopyToClipboard(text) {
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      console.log('URL copied using fallback method:', text);
      this.showCopySuccess();
    } else {
      console.error('Fallback copy failed');
    }
  } catch (err) {
    console.error('Fallback copy error:', err);
  }
}

// Show a brief success message when URL is copied
showCopySuccess() {
  const urlPreview = document.getElementById('urlPreview');
  if (!urlPreview) return;
  
  const successMsg = document.createElement('div');
  successMsg.className = 'copy-success';
  successMsg.textContent = '✓ Copied!';
  successMsg.style.cssText = `
    position: absolute;
    background: #4CAF50;
    color: white;
    padding: 4px 8px;
    border-radius: 3px;
    font-size: 12px;
    margin-top: 5px;
    z-index: 1000;
  `;
  
  urlPreview.appendChild(successMsg);
  
  setTimeout(() => {
    if (successMsg.parentNode) {
      successMsg.parentNode.removeChild(successMsg);
    }
  }, 2000);
}

async initializeBNSToggle(pageData) {
  const bnsToggle = document.getElementById('bnsEnabled');
  const bnsFields = document.getElementById('bnsFields');
  const bnsDropdown = document.getElementById('bnsDropdown');

  if (!bnsToggle || !bnsFields) return;

  // Enhanced BNS name detection - check all possible locations
  let currentBNSName = null;
  
  // Check in metadata.properties.meta.content.bnsName (your current location)
  if (pageData.metadata?.properties?.meta?.content?.bnsName) {
    currentBNSName = pageData.metadata.properties.meta.content.bnsName;
  }
  // Check in metadata.bns_name (direct property)
  else if (pageData.metadata?.bns_name) {
    currentBNSName = pageData.metadata.bns_name;
  }
  // Check in attributes array for 'BNS Name' trait
  else if (pageData.metadata?.attributes) {
    const bnsAttribute = pageData.metadata.attributes.find(attr => 
      attr.trait_type === 'BNS Name' && attr.value
    );
    if (bnsAttribute) {
      currentBNSName = bnsAttribute.value;
    }
  }

  const hasBNSName = currentBNSName && currentBNSName.length > 0;

  console.log('BNS Detection:', {
    currentBNSName,
    hasBNSName,
    metadataStructure: {
      contentBNS: pageData.metadata?.properties?.meta?.content?.bnsName,
      directBNS: pageData.metadata?.bns_name,
      attributes: pageData.metadata?.attributes
    }
  });

  // Set the toggle state based on whether BNS name exists
  bnsToggle.checked = hasBNSName;

  // Show/hide the fields based on toggle state
  bnsFields.style.display = bnsToggle.checked ? 'block' : 'none';

  // Load owned BNS names when toggle is enabled or when we have a current BNS name
  if (bnsToggle.checked || hasBNSName) {
    await this.loadOwnedBNSNames();
    
    // After loading, try to select the current BNS name
    if (hasBNSName && bnsDropdown) {
      await this.selectCurrentBNSName(currentBNSName);
    }
  }

  // Add event listener for toggle changes
  bnsToggle.addEventListener('change', async (e) => {
    const isChecked = e.target.checked;
    bnsFields.style.display = isChecked ? 'block' : 'none';

    if (isChecked) {
      await this.loadOwnedBNSNames();
      
      // If we have a current BNS name, try to select it
      if (currentBNSName) {
        await this.selectCurrentBNSName(currentBNSName);
      }
    }

    // Update the data model
    if (!pageData.metadata) pageData.metadata = {};
    if (!pageData.metadata.properties) pageData.metadata.properties = {};
    if (!pageData.metadata.properties.meta) pageData.metadata.properties.meta = {};
    if (!pageData.metadata.properties.meta.content) pageData.metadata.properties.meta.content = {};
    
    pageData.metadata.properties.meta.content.bnsEnabled = isChecked;
    
    if (!isChecked) {
      // Clear BNS name when disabled
      pageData.metadata.properties.meta.content.bnsName = '';
    }

    // Show blockchain warning
    this.blockchainWarning.style.display = 'flex';
  });

  // Add event listener for BNS selection changes
  if (bnsDropdown) {
    bnsDropdown.addEventListener('change', (e) => {
      const selectedBNS = e.target.value;
      
      // Update the data model
      if (pageData.metadata?.properties?.meta?.content) {
        pageData.metadata.properties.meta.content.bnsName = selectedBNS;
      }

      // Update the page URL preview
      this.updateURLPreview(selectedBNS);

      // Show blockchain warning
      this.blockchainWarning.style.display = 'flex';
    });
  }
}

// Add this new method to handle selecting the current BNS name
async selectCurrentBNSName(bnsName) {
  const bnsDropdown = document.getElementById('bnsDropdown');
  if (!bnsDropdown || !bnsName) return;

  console.log('Attempting to select BNS name:', bnsName);

  // Wait a bit for the dropdown to be populated
  await new Promise(resolve => setTimeout(resolve, 100));

  // Try to find and select the matching option
  let optionFound = false;
  for (let i = 0; i < bnsDropdown.options.length; i++) {
    const option = bnsDropdown.options[i];
    if (option.value === bnsName || option.textContent === bnsName) {
      bnsDropdown.selectedIndex = i;
      optionFound = true;
      console.log(`Selected BNS option: ${bnsName} at index ${i}`);
      
      // Trigger the change event to update URL preview
      const changeEvent = new Event('change', { bubbles: true });
      bnsDropdown.dispatchEvent(changeEvent);
      break;
    }
  }

  if (!optionFound) {
    console.warn(`BNS name "${bnsName}" not found in dropdown options`);
    
    // Optionally add the BNS name as an option if it's not found
    // This handles cases where the BNS name exists in metadata but isn't in the owned list
    const option = document.createElement('option');
    option.value = bnsName;
    option.textContent = bnsName;
    option.selected = true;
    bnsDropdown.appendChild(option);
    
    console.log(`Added missing BNS name "${bnsName}" to dropdown`);
    
    // Trigger the change event
    const changeEvent = new Event('change', { bubbles: true });
    bnsDropdown.dispatchEvent(changeEvent);
  }
}

async loadOwnedBNSNames() {
  const bnsDropdown = document.getElementById('bnsDropdown');
  if (!bnsDropdown) return;

  try {
    // Clear existing options except the first one
    while (bnsDropdown.options.length > 1) {
      bnsDropdown.removeChild(bnsDropdown.options[1]);
    }

    // Get the current user's address
    const currentAddress = this.router.authService?.currentAddress;
    if (!currentAddress) {
      console.warn('No current address available for BNS lookup');
      return;
    }

    console.log('Loading BNS names for address:', currentAddress);

    // Fetch BNS names owned by this address
    const bnsNames = await this.fetchOwnedBNSNames(currentAddress);
    
    if (bnsNames && bnsNames.length > 0) {
      bnsNames.forEach(bnsName => {
        const option = document.createElement('option');
        option.value = bnsName;
        option.textContent = bnsName;
        bnsDropdown.appendChild(option);
      });
      
      console.log(`Loaded ${bnsNames.length} BNS names:`, bnsNames);
    } else {
      // Add a message indicating no BNS names found
      const option = document.createElement('option');
      option.value = '';
      option.textContent = 'No BNS names owned';
      option.disabled = true;
      bnsDropdown.appendChild(option);
      
      console.log('No BNS names found for this address');
    }
  } catch (error) {
    console.error('Error loading BNS names:', error);
    
    // Add error option
    const option = document.createElement('option');
    option.value = '';
    option.textContent = 'Error loading BNS names';
    option.disabled = true;
    bnsDropdown.appendChild(option);
  }
}

// Update your fetchOwnedBNSNames method to properly parse BNSv2 API response

async fetchOwnedBNSNames(userAddress) {
  try {
    console.log('Fetching BNS names for address:', userAddress);
    
    // Use BNSv2 API
    const bnsApiUrl = `https://api.bnsv2.com/names/address/${userAddress}/valid`;
    console.log('Fetching from:', bnsApiUrl);
    
    const response = await fetch(bnsApiUrl);
    
    if (!response.ok) {
      throw new Error(`BNS API error: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Raw BNS API response:', data);
    
    // Parse the response - it could be in different formats
    let bnsNames = [];
    
    // Check if data is an array of objects
    if (Array.isArray(data)) {
      bnsNames = data.map(item => this.extractBNSName(item)).filter(name => name);
    }
    // Check if data has a names property
    else if (data.names && Array.isArray(data.names)) {
      bnsNames = data.names.map(item => this.extractBNSName(item)).filter(name => name);
    }
    // Check if data has results property
    else if (data.results && Array.isArray(data.results)) {
      bnsNames = data.results.map(item => this.extractBNSName(item)).filter(name => name);
    }
    // If data is a single object with BNS info
    else if (typeof data === 'object' && data !== null) {
      const name = this.extractBNSName(data);
      if (name) bnsNames = [name];
    }
    
    console.log('Extracted BNS names:', bnsNames);
    return bnsNames;
    
  } catch (error) {
    console.error('Error fetching BNS names:', error);
    
    // Fallback: try alternative BNS APIs or methods
    return await this.fallbackBNSFetch(userAddress);
  }
}

// Helper method to extract BNS name from different object structures
extractBNSName(item) {
  if (!item || typeof item !== 'object') return null;
  
  // Common patterns for BNS name storage:
  
  // Pattern 1: Direct name property
  if (item.name) {
    return item.name;
  }
  
  // Pattern 2: Combined name.namespace format
  if (item.name && item.namespace) {
    return `${item.name}.${item.namespace}`;
  }
  
  // Pattern 3: fullName property
  if (item.fullName) {
    return item.fullName;
  }
  
  // Pattern 4: full_name property (snake_case)
  if (item.full_name) {
    return item.full_name;
  }
  
  // Pattern 5: Nested in name object
  if (item.name && typeof item.name === 'object') {
    if (item.name.name && item.name.namespace) {
      return `${item.name.name}.${item.name.namespace}`;
    }
    if (item.name.combined) {
      return item.name.combined;
    }
  }
  
  // Pattern 6: id property (sometimes contains the full name)
  if (item.id && typeof item.id === 'string' && item.id.includes('.')) {
    return item.id;
  }
  
  // Pattern 7: Check for common BNS name pattern in any string property
  const stringProps = Object.values(item).filter(val => typeof val === 'string');
  for (const prop of stringProps) {
    if (this.isBNSNameFormat(prop)) {
      return prop;
    }
  }
  
  console.warn('Could not extract BNS name from item:', item);
  return null;
}

// Helper to check if a string looks like a BNS name
isBNSNameFormat(str) {
  if (!str || typeof str !== 'string') return false;
  
  // BNS name pattern: name.namespace
  const bnsPattern = /^[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+$/;
  return bnsPattern.test(str);
}

// Fallback method to try other BNS APIs or approaches
async fallbackBNSFetch(userAddress) {
  const fallbackApis = [
    // Alternative API endpoints you could try
    `https://stacks-node-api.mainnet.stacks.co/extended/v1/names/owned_by/${userAddress}`,
    `https://api.hiro.so/extended/v1/names/owned_by/${userAddress}`,
  ];
  
  for (const apiUrl of fallbackApis) {
    try {
      console.log('Trying fallback API:', apiUrl);
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Fallback API response:', data);
        
        // Try to parse this response too
        if (Array.isArray(data)) {
          return data.map(item => this.extractBNSName(item)).filter(name => name);
        }
      }
    } catch (error) {
      console.warn('Fallback API failed:', apiUrl, error);
    }
  }
  
  // If all APIs fail, return empty array
  console.warn('All BNS APIs failed, returning empty array');
  return [];
}

// Debug method to help understand the API response structure
debugBNSResponse(data) {
  console.log('=== BNS API Response Debug ===');
  console.log('Type:', typeof data);
  console.log('Is Array:', Array.isArray(data));
  
  if (Array.isArray(data)) {
    console.log('Array length:', data.length);
    if (data.length > 0) {
      console.log('First item:', data[0]);
      console.log('First item keys:', Object.keys(data[0] || {}));
    }
  } else if (typeof data === 'object' && data !== null) {
    console.log('Object keys:', Object.keys(data));
    console.log('Full object:', data);
  }
  
  console.log('=== End Debug ===');
}

}

// Content Block Manager for EditSidebar
export class ContentBlockManager {
  constructor() {
    this.blocks = [];
    this.initDragAndDrop();
  }


  // Initialize drag and drop functionality
  initDragAndDrop() {
    // Add the content blocks section to the sidebar
    const contentBlocksHTML = `
     
    `;

    // Insert the content blocks section into the sidebar
    const sidebar = document.querySelector('.edit-sidebar-content');
    const form = sidebar.querySelector('form');
    form.insertAdjacentHTML('beforeend', contentBlocksHTML);

    // Add event listeners for drag and drop
    this.setupDragAndDrop();

    // Add styles
    this.addStyles();
  }


  // Set up drag and drop event listeners
  setupDragAndDrop() {

    const draggableBlocks = document.querySelectorAll('.content-block');
    const dropArea = document.querySelector('.blocks-container');
    const sidebar = document.querySelector('.edit-sidebar-content');
    const blockchainStatus = document.getElementById('blockchainStatus');

    draggableBlocks.forEach(block => {
      block.addEventListener('dragstart', (e) => {
        e.dataTransfer.setData('text/plain', block.getAttribute('data-type'));
        block.classList.add('dragging');
      });

      block.addEventListener('dragend', () => {
        block.classList.remove('dragging');
      });
    });

    dropArea.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropArea.classList.add('drag-over');
    });

    dropArea.addEventListener('dragleave', () => {
      dropArea.classList.remove('drag-over');
    });

    dropArea.addEventListener('drop', (e) => {
      e.preventDefault();
      dropArea.classList.remove('drag-over');
      const blockType = e.dataTransfer.getData('text/plain');
      console.log('DropArea detected blockType:', blockType);

      if (blockType) {
        blockchainStatus.style.display = 'block';
        this.createContentBlock(blockType, dropArea);
      }
    });
  }

  // Create a new content block in the drop area

  // Get the current blocks data
  getBlocksData() {
    return this.blocks;
  }

  // Add styles for the content blocks
  addStyles() {
    const styles = `
      .external-link-preview {
      margin: 15px 0;
      max-width: 100%;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .preview-card {
      display: flex;
      border: 1px solid #444;
      border-radius: 8px;
      overflow: hidden;
      background-color: #222;
      color: #fff;
      transition: box-shadow 0.2s ease;
      min-width: 300px;
      max-width: 500px;
      flex-shrink: 0;
      margin-right: 15px;
    }

    .preview-card:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    .preview-image {
      max-width: 120px;
      object-fit: cover;
      background-color: #333;
    }

    .preview-content {
      padding: 12px;
      flex: 1;
      min-width: 0; /* Helps with text truncation */
    }

    .preview-content h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      color: #fff;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .preview-content p {
      margin: 0 0 10px 0;
      font-size: 14px;
      color: #ccc;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .preview-link {
      display: inline-block;
      color: #3897f0;
      font-size: 14px;
      text-decoration: none;
    }

    .preview-link:hover {
      text-decoration: underline;
    }

    /* Loading state */
    .external-link-preview > p {
      padding: 10px;
      background-color: #222;
      border-radius: 6px;
      color: #ccc;
      margin: 0;
    }

    /* Multiple previews scrolling styles */
    .previews-scroll-container {
      width: 100%;
      max-width: 100%;
      overflow: hidden;
    }

    .previews-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      color: #ccc;
      font-size: 14px;
    }

    .previews-controls {
      display: flex;
      gap: 5px;
    }

    .preview-scroll-left,
    .preview-scroll-right {
      background-color: #333;
      border: none;
      color: white;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .preview-scroll-left:hover,
    .preview-scroll-right:hover {
      background-color: #444;
    }

    .preview-scroll-left:disabled,
    .preview-scroll-right:disabled {
      cursor: not-allowed;
    }

    .previews-track {
      display: flex;
      overflow-x: auto;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: thin;
      scrollbar-color: #444 #222;
      padding-bottom: 10px;
      gap: 10px;
    }

    /* Hide scrollbar for cleaner look */
    .previews-track::-webkit-scrollbar {
      height: 6px;
    }

    .previews-track::-webkit-scrollbar-track {
      background-color: #222;
      border-radius: 3px;
    }

    .previews-track::-webkit-scrollbar-thumb {
      background-color: #444;
      border-radius: 3px;
    }
     .image-placeholder {
      max-height: 300px;
      object-fit: cover;
      border: 1px solid #ddd;
      border-radius: 4px;
      margin: 10px 0;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .image-placeholder:hover {
      opacity: 0.8;
    }

    .image-placeholder.drag-over {
      border: 2px dashed #0078ff;
      background-color: rgba(0, 120, 255, 0.1);
    }

    .image-upload-wrapper {
      cursor: pointer;
      display: block;
      width: 100%;
    }

    /* File input for image blocks (hidden) */
    .image-input {
      display: none;
    }

    .drop-indicator {
      color: #666;
      font-size: 16px;
    }

    .featured-image {
  width: 100%;
  height: 300px; /* Default height - can be adjusted */
  object-fit: cover;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.featured-image:hover {
  opacity: 0.9;
}

.featured-image.drag-over {
  border: 2px dashed #0078ff;
  background-color: rgba(0, 120, 255, 0.1);
}

/* Make the block controls more visible on top of the image */
.featured-image-block{
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  padding: 2px;
  z-index: 10;
}

  .external-link-preview {
      background-color: transparent;
    }

    .preview-card {
      display: flex;
      border: 1px solid #333;
      border-radius: 8px;
      overflow: hidden;
      background-color: #222;
      color: #fff;
    }


    .preview-content {
      padding: 12px;
      flex: 1;
    }

    .preview-content h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      color: #fff;
    }

    .preview-content p {
      margin: 0 0 10px 0;
      font-size: 14px;
      color: #ccc;
      line-height: 1.4;
    }

    .preview-link {
      font-family: 'Helvetica';
      display: inline-block;
      color: #3897f0;
      font-size: 14px;
      text-decoration: none;
    }

    /* Responsive adjustments */
@media (min-width: 768px) {
  .featured-image {
    height: 400px; /* Taller on larger screens */
  }
}

@media (min-width: 1200px) {
  .featured-image {
    height: 500px; /* Even taller on very large screens */
  }
      .external-link-preview {
      background-color: transparent;
    }

    .preview-card {
      display: flex;
      border: 1px solid #333;
      border-radius: 8px;
      overflow: hidden;
      background-color: #222;
      color: #fff;
    }

   

    .preview-content {
      padding: 12px;
      flex: 1;
    }

    .preview-content h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      color: #fff;
    }

    .preview-content p {
      margin: 0 0 10px 0;
      font-size: 14px;
      color: #ccc;
      line-height: 1.4;
    }

    /* Loading state */
    .external-link-preview > p {
      padding: 10px;
      background-color: #222;
      border-radius: 6px;
      color: #ccc;
      margin: 0;
    }

    .preview-link:hover {
      text-decoration: underline;
    }

    /* Loading state */
    .external-link-preview > p {
      padding: 10px;
      background-color: #222;
      border-radius: 6px;
      color: #ccc;
      margin: 0;
    }
      .content-blocks-section {
        margin-top: 20px;
        border-top: 1px solid #ddd;
        padding-top: 20px;
      }

 .content-editable-area.accept-drop {
      background-color: rgba(0, 120, 255, 0.1);
      border: 2px dashed #0078ff;
    }

    .image-placeholder.drag-over {
      border: 2px dashed #0078ff;
      background-color: rgba(0, 120, 255, 0.1);
    }

    .image-placeholder {
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .image-placeholder:hover {
      opacity: 0.8;
    }

      .content-blocks {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
      }

      .content-block {
        border: 2px dashed #ccc;
        padding: 10px;
        border-radius: 4px;
        cursor: move;
        background: #f9f9f9;
        width: 100px;
        text-align: center;
      }

      .content-block.dragging {
        opacity: 0.5;
      }

      .block-preview {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;
      }

      .block-icon {
        font-size: 24px;
        color: #666;
      }

      .page-content-area {
        border: 2px dashed #ddd;
        min-height: 200px;
        padding: 20px;
        margin-top: 20px;
        border-radius: 4px;
      }

      .content-drop-area {
        text-align: center;
        color: #999;
        padding: 20px;
        display: none;
      }

      .blocks-container:empty + .content-drop-area {
        display: block;
      }

      .block-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        padding-bottom: 5px;
        border-bottom: 1px solid #eee;
      }

      .block-type {
        font-size: 12px;
        color: #666;
      }


    /* Link Preview Styles */
    .link-preview {
      margin-top: 10px;
      font-size: 14px;
    }

    .link-preview img {
      border-radius: 4px;
    }

    .link-preview strong {
      display: block;
      margin-bottom: 2px;
      color: #333;
    }

    .link-preview p {
      color: #666;
      margin: 4px 0;
      font-size: 13px;
      line-height: 1.4;
    }

    .link-preview a {
      color: #0078ff;
      text-decoration: none;
      font-size: 12px;
    }

    .link-preview a:hover {
      text-decoration: underline;
    }

    /* Loading state */
    .link-preview p:first-child {
      color: #999;
      font-style: italic;
    }

      .delete-block {
        cursor: move;
        padding: 4px 8px;
        background: #f0f0f0;
        border: 1px solid #ddd;
        border-radius: 4px;
        color: #333;
      }

      .delete-block:hover {
        color: #ff4444;
      }

      .heading-input,
      .paragraph-input {
        width: 100%;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        margin-top: 5px;
      }

      .paragraph-input {
        min-height: 100px;
        resize: vertical;
      }

      .drag-over {
        background-color: #f0f8ff;
        border-color: #66afe9;
      }
    `;

    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
  }
}

// InlineContentEditor class for direct page editing
export class InlineContentEditor {
  constructor(sidebarInstance) {
    this.sidebar = sidebarInstance; // Store reference to sidebar
    this.init();
    this.blockCount = 0;
    this.draggedBlock = null;
  }


  init() {
    console.log('Starting init() method');

    const contentWrapper = document.querySelector('.content-wrapper');
    const mainContent = document.querySelector('.page-content') || document.body;
    mainContent.classList.add('content-editable-area');

    // Create a hidden drop zone
    this.createDropZone();

    // Set up drag and drop from sidebar
    this.setupSidebarDrag();

    // Set up global drag and drop for images
    this.setupGlobalImageDrop();

    // Add styles
    this.addStyles();
    console.log('Finished init() method');
  }

  createExternalLinkPreview(blockId) {
    const mainContent = document.querySelector('.page-content') || document.body;

    // Check if a preview already exists for this block
    const existingPreview = document.querySelector(`.external-link-preview[data-for="${blockId}"]`);
    if (existingPreview) {
      return existingPreview;
    }

    // Create a new preview container
    const previewContainer = document.createElement('div');
    previewContainer.className = 'external-link-preview';
    previewContainer.dataset.for = blockId; // Associate with the block
    previewContainer.style.display = 'none'; // Hidden by default

    // Add to the main content area (outside the modal)
    mainContent.appendChild(previewContainer);

    return previewContainer;
  }

  async updateExternalLinkPreview(blockId, urls) {
    const previewContainer = document.querySelector(`.external-link-preview[data-for="${blockId}"]`);

    if (!previewContainer) {
      console.error('Preview container not found for block:', blockId);
      return;
    }

    if (urls && urls.length > 0) {
      previewContainer.style.display = 'block';
      previewContainer.innerHTML = '<p>Loading previews...</p>';

      try {
        // Check if the fetchLinkPreview method exists
        if (typeof this.fetchLinkPreview !== 'function') {
          console.error('fetchLinkPreview method is not defined');
          previewContainer.innerHTML = '<p style="color:red;">Link preview functionality not available.</p>';
          return;
        }

        // If only one URL, show it directly
        if (urls.length === 1) {
          const preview = await this.fetchLinkPreview(urls[0]);
          previewContainer.innerHTML = this.createPreviewCard(preview, urls[0]);
          return;
        }

        // For multiple URLs, create a carousel/scrollable container
        const previewsHTML = [];
        const fetchPromises = urls.map(url => this.fetchLinkPreview(url));

        // Use Promise.allSettled to handle any errors without stopping other previews
        const results = await Promise.allSettled(fetchPromises);

        previewsHTML.push('<div class="previews-scroll-container">');
        previewsHTML.push('<div class="previews-header">');
        previewsHTML.push(`<span>${urls.length} links found</span>`);
        previewsHTML.push('<div class="previews-controls">');
        previewsHTML.push('<button class="preview-scroll-left">←</button>');
        previewsHTML.push('<button class="preview-scroll-right">→</button>');
        previewsHTML.push('</div></div>'); // Close header divs

        previewsHTML.push('<div class="previews-track">');

        // Process each preview result
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            previewsHTML.push(this.createPreviewCard(result.value, urls[index]));
          } else {
            // Handle failed preview
            previewsHTML.push(`
            <div class="preview-card">
              <div class="preview-content">
                <h3>${urls[index]}</h3>
                <p>Failed to load preview</p>
                <a href="${urls[index]}" target="_blank" class="preview-link">Visit site</a>
              </div>
            </div>
          `);
          }
        });

        previewsHTML.push('</div>'); // Close track div
        previewsHTML.push('</div>'); // Close container div

        previewContainer.innerHTML = previewsHTML.join('');

        // Add scroll functionality for previews
        this.setupPreviewScrolling(previewContainer);

      } catch (err) {
        console.error('Failed to fetch previews:', err);
        previewContainer.innerHTML = '<p style="color:red;">Failed to load previews.</p>';
      }
    } else {
      previewContainer.style.display = 'none';
      previewContainer.innerHTML = '';
    }
  }

  extractUrl(text) {
    // More comprehensive regex that can detect URLs with or without protocol
    const urlRegex = /(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?(\/[^\s]*)?/gi;

    const matches = text.match(urlRegex);

    // Return all URLs found, or null if none
    if (!matches) return [];

    // Add https:// protocol to all URLs that are missing it
    const urls = matches.map(url => {
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return 'https://' + url;
      }
      return url;
    });

    console.log('Extracted URLs:', urls);
    return urls;
  }


  createPreviewCard(preview, url) {
    // Extract domain for display
    let domain = '';
    try {
      domain = new URL(url).hostname.replace('www.', '');
    } catch (e) {
      domain = url;
    }

    // Use favicon from API or fallback to Google's favicon service
    const favicon = preview.favicon || `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

    // Determine if we have a valid image
    const hasImage = preview.image && preview.image.trim() !== '';

    // Image HTML with error handling
    const imageHTML = hasImage ? `
  
  ` : '';

    return `
    <div class="preview-card">
      <div class="preview-header">
        <img 
          src="${favicon}" 
          class="preview-favicon" 
          alt="${domain} favicon"
          onerror="this.onerror=null; this.src='https://www.google.com/s2/favicons?domain=${domain}&sz=128';"
        >
      
      </div>
      
      <div class="preview-content-wrapper">
        ${imageHTML}
        
        <div class="preview-content">
          <h3 class="preview-title">${preview.title || 'Visit Website'}</h3>
          <p class="preview-description">${preview.description || ''}</p>
          <a href="${url}" target="_blank" rel="noopener noreferrer" class="preview-link-button">
            Visit Full Site
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="external-link-icon">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </a>
        </div>
      </div>
    </div>
  `;
  }


  setupPreviewScrolling(container) {
    const track = container.querySelector('.previews-track');
    const leftBtn = container.querySelector('.preview-scroll-left');
    const rightBtn = container.querySelector('.preview-scroll-right');

    if (!track || !leftBtn || !rightBtn) return;

    // Set initial scroll position
    let currentIndex = 0;
    const cards = track.querySelectorAll('.preview-card');

    // Update button states
    const updateButtons = () => {
      leftBtn.disabled = currentIndex <= 0;
      rightBtn.disabled = currentIndex >= cards.length - 1;

      leftBtn.style.opacity = leftBtn.disabled ? 0.5 : 1;
      rightBtn.style.opacity = rightBtn.disabled ? 0.5 : 1;
    };

    updateButtons();

    // Scroll to a specific card
    const scrollToCard = (index) => {
      if (index < 0) index = 0;
      if (index >= cards.length) index = cards.length - 1;

      currentIndex = index;
      const card = cards[index];

      if (card) {
        // Smooth scroll to the card
        track.scrollTo({
          left: card.offsetLeft,
          behavior: 'smooth'
        });

        // Update button states
        updateButtons();
      }
    };

    // Event listeners for buttons
    leftBtn.addEventListener('click', () => {
      scrollToCard(currentIndex - 1);
    });

    rightBtn.addEventListener('click', () => {
      scrollToCard(currentIndex + 1);
    });

    // Optional: keyboard navigation for accessibility
    container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        scrollToCard(currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        scrollToCard(currentIndex + 1);
      }
    });
  }

  setupGlobalImageDrop() {
    const mainContent = document.querySelector('.page-content') || document.body;

    mainContent.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (e.dataTransfer.types.includes('Files')) {
        mainContent.classList.add('accept-drop');
      }
    });

    mainContent.addEventListener('dragleave', (e) => {
      // Only remove the class if we're leaving the main content area
      if (!mainContent.contains(e.relatedTarget)) {
        mainContent.classList.remove('accept-drop');
      }
    });

    mainContent.addEventListener('drop', (e) => {
      e.preventDefault();
      mainContent.classList.remove('accept-drop');

      const target = e.target;
      const isImagePlaceholder = target.classList.contains('image-placeholder');
      const imageBlock = isImagePlaceholder ?
        target.closest('.editable-block[data-type="image"]') :
        null;

      // ✅ Check if dropped data is a "text/plain" (meaning it's a draggable block, not a file)
      const draggedType = e.dataTransfer.getData('text/plain');

      if (draggedType) {
        console.log('Detected dragged block type:', draggedType);
        // It's a draggable content block (like paragraph/heading)
        return; // ⬅️ Important! Exit early, don't continue to image logic
      }

      // ✅ Otherwise treat it like an image file drop
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const file = e.dataTransfer.files[0];
        if (file.type.startsWith('image/')) {
          if (imageBlock) {
            const imgElement = imageBlock.querySelector('.image-placeholder');
            if (imgElement) {
              imgElement.src = URL.createObjectURL(file);
              return;
            }
          }

          this.createImageBlock(file);
          this.createFeaturedImageBlock(file);
        }
      }
    });

  }

  // Fetch link preview data using the Link Preview API
  async fetchLinkPreview(url) {
    try {
      // Use the external API instead of the mock implementation
      const response = await fetch(`https://favorited-link-preview.herokuapp.com/api/link-preview?url=${encodeURIComponent(url)}`);
      const data = await response.json();

      // Check if the request was successful
      if (!data.result) {
        console.error('API returned an error:', data.error || data.errors);
        throw new Error('Failed to fetch link preview');
      }

      // Transform the API response to match your expected format
      const apiResult = data.result;
      const siteData = apiResult.siteData || {};

      // Determine the best image to use
      let bestImage = null;
      if (apiResult.topImage) {
        bestImage = apiResult.topImage;
      } else if (siteData.largestImage) {
        bestImage = siteData.largestImage;
      } else if (siteData.image) {
        bestImage = siteData.image;
      } else if (apiResult.imageResults && apiResult.imageResults.length > 0) {
        bestImage = apiResult.imageResults[0];
      }

      return {
        title: siteData.title || new URL(url).hostname,
        description: siteData.description || `Visit ${new URL(url).hostname} to learn more.`,
        image: bestImage,
        favicon: siteData.favicon,
        siteName: siteData.siteName,
        url: url
      };
    } catch (error) {
      console.error('Error fetching link preview:', error);

      // Fallback to basic information when API fails
      let domain = '';
      try {
        domain = new URL(url).hostname.replace('www.', '');
      } catch (e) {
        domain = url;
      }

      return {
        title: domain.charAt(0).toUpperCase() + domain.slice(1),
        description: `Visit ${domain} to learn more.`,
        image: `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
        url: url
      };
    }
  }


  // First, create a Map to store image files at the class/module level
  // Add this outside your method, as a class property or module-level variable

  createImageBlock(file = null) {
    const blockId = `block-${this.blockCount++}`;
    const block = document.createElement('div');
    block.className = 'editable-block';
    block.dataset.type = 'image';
    block.id = blockId;
    block.draggable = true;

    // Set default image path
    const defaultImagePath = '/images/gated-logo2.png';

    // Initialize image source
    let imageSrc = defaultImagePath;

    // If we're passed a file, create a blob URL and store the file
    if (file) {
      imageSrc = URL.createObjectURL(file);
      // Store the file in our Map using the block ID as the key
      imageFiles.set(blockId, file);
    }

    block.innerHTML = `
    <div class="block-controls1">
      <button class="move-block">≡</button>
      <button class="align-block">↔</button>
      <button class="delete-block">×</button>
    </div>
  
    <div class="image-upload-wrapper">
      <img src="${imageSrc}" class="image-placeholder" alt="Content image" data-default="${defaultImagePath}">
      <input type="file" class="image-input" accept="image/*">
    </div>
  `;

    // First append the block to DOM
    const mainContent = document.querySelector('.page-content') || document.body;
    mainContent.appendChild(block);

    // Now add event listeners when we're sure elements exist
    const deleteBtn = block.querySelector('.delete-block');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => {
        // Also remove any stored file when deleting the block
        imageFiles.delete(blockId);
        block.remove();
      });
    }

    // Set up image upload functionality
    const imageInput = block.querySelector('.image-input');
    const imagePlaceholder = block.querySelector('.image-placeholder');

    // Add event listeners only if elements exist
    if (imagePlaceholder && imageInput) {
      // When clicking on the image, trigger the file input
      imagePlaceholder.addEventListener('click', () => {
        imageInput.click();
      });

      // When a file is selected, update the image
      imageInput.addEventListener('change', (e) => {
        if (e.target.files && e.target.files[0]) {
          const newFile = e.target.files[0];

          // Create and set blob URL for display
          const blobUrl = URL.createObjectURL(newFile);
          imagePlaceholder.src = blobUrl;

          // Store the file for later IPFS upload
          imageFiles.set(blockId, newFile);

          // Mark this image as needing upload
          imagePlaceholder.dataset.pendingUpload = "true";
        }
      });

      // Add support for drag and drop directly on the image
      imagePlaceholder.addEventListener('dragover', (e) => {
        e.preventDefault();
        imagePlaceholder.classList.add('drag-over');
      });

      imagePlaceholder.addEventListener('dragleave', () => {
        imagePlaceholder.classList.remove('drag-over');
      });

      imagePlaceholder.addEventListener('drop', (e) => {
        e.preventDefault();
        imagePlaceholder.classList.remove('drag-over');

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
          const droppedFile = e.dataTransfer.files[0];
          if (droppedFile.type.startsWith('image/')) {
            // Create and set blob URL for display
            const blobUrl = URL.createObjectURL(droppedFile);
            imagePlaceholder.src = blobUrl;

            // Store the file for later IPFS upload
            imageFiles.set(blockId, droppedFile);

            // Mark this image as needing upload
            imagePlaceholder.dataset.pendingUpload = "true";
          }
        }
      });
    }

    // Set up drag handlers for reordering blocks
    this.setupDragHandlers(block);

    return block;
  }
  // Update the createEditableBlock to handle featured image blocks
  createEditableBlock(type) {
    // Handle different block types
    if (type === 'image') {
      return this.createImageBlock();
    }

    const blockId = `block-${this.blockCount++}`;
    const block = document.createElement('div');
    block.className = 'editable-block';
    block.dataset.type = type;
    block.id = blockId;
    block.draggable = true;

    const defaultText = type === 'heading' ? 'New Heading' : 'New paragraph';

    if (type === 'heading') {
      block.innerHTML = `
      <div class="block-controls1">
      
        <button class="move-block">≡</button>
        <button class="align-block">↔</button>
        <button class="delete-block">×</button>
      </div>
      <h2 class="editable" contenteditable="true" placeholder="Enter heading">${defaultText}</h2>
    `;
    } else if (type === 'paragraph') {
      block.innerHTML = `
      <div class="block-controls1">
        <button class="move-block">≡</button>
        <button class="align-block">↔</button>
        <button class="delete-block">×</button>
      </div>
      <p class="editable" contenteditable="true" placeholder="Enter text">${defaultText}</p>
    `;
    }

    // First insert the block before adding event listeners
    const mainContent = document.querySelector('.page-content') || document.body;
    mainContent.appendChild(block);

    // Now that the block is in the DOM, add event listeners
    const deleteBtn = block.querySelector('.delete-block');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => {
        block.remove();
      });
    }

    // Add event listeners for editing and controls
    this.setupDragHandlers(block);

    // Close sidebar
    if (this.sidebar && typeof this.sidebar.hide === 'function') {
      this.sidebar.hide();
    }

    // Focus and select text
    const editable = block.querySelector('.editable');
    if (editable) {
      setTimeout(() => {
        editable.focus();
        // Create selection range
        const range = document.createRange();
        range.selectNodeContents(editable);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }, 100); // Small delay to ensure DOM is ready
    }

    return block;
  }

  // Add this improved setupDragHandlers method to your InlineContentEditor class
  setupDragHandlers(block) {
    // Set up drag start event
    block.addEventListener('dragstart', (e) => {
      e.dataTransfer.setData('text/plain', block.id);
      block.classList.add('dragging');
      this.draggedBlock = block;

      // Add visual feedback
      block.style.opacity = '0.5';

      // Show drop indicators on other blocks
      this.showDropIndicators();
    });

    // Set up drag end event
    block.addEventListener('dragend', (e) => {
      block.classList.remove('dragging');
      block.style.opacity = '1';
      this.draggedBlock = null;

      // Remove all drop indicators
      this.removeDropIndicators();
    });

    // Set up drag over event (for indicating drop positions)
    block.addEventListener('dragover', (e) => {
      e.preventDefault();

      if (!this.draggedBlock || this.draggedBlock === block) return;

      const rect = block.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;

      // Clear previous indicators
      this.removeDropIndicators();

      // Add drop indicator based on cursor position
      if (e.clientY < midpoint) {
        block.classList.add('drop-before');
      } else {
        block.classList.add('drop-after');
      }

      block.classList.add('drag-target');
    });

    // Set up drop event
    block.addEventListener('drop', (e) => {
      e.preventDefault();

      if (!this.draggedBlock || this.draggedBlock === block) return;

      const rect = block.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      const dropBefore = e.clientY < midpoint;

      // Reposition the dragged block
      if (dropBefore) {
        block.parentNode.insertBefore(this.draggedBlock, block);
      } else {
        // Insert after this block
        const nextBlock = block.nextElementSibling;
        if (nextBlock) {
          block.parentNode.insertBefore(this.draggedBlock, nextBlock);
        } else {
          block.parentNode.appendChild(this.draggedBlock);
        }
      }

      // Remove indicators
      this.removeDropIndicators();

      // Reset dragged block state
      this.draggedBlock.style.opacity = '1';
      this.draggedBlock = null;
    });

    // Handle move button click for both mouse and touch devices
    const moveBtn = block.querySelector('.move-block');
    if (moveBtn) {
      // Make the move button the drag handle instead of the whole block
      moveBtn.draggable = true;
      block.draggable = false;

      // When the move button starts dragging, transfer the drag to the parent block
      moveBtn.addEventListener('dragstart', (e) => {
        // Set data for the drag operation
        e.dataTransfer.setData('text/plain', block.id);

        // Add visual feedback
        block.classList.add('dragging');
        block.style.opacity = '0.5';

        // Store reference to dragged block
        this.draggedBlock = block;

        // Show drop indicators on other blocks
        this.showDropIndicators();

        // Allow the drag to continue
        e.stopPropagation();
      });

      // Stop event propagation on the button itself
      moveBtn.addEventListener('dragend', (e) => {
        e.stopPropagation();

        // Clean up
        block.classList.remove('dragging');
        block.style.opacity = '1';
        this.draggedBlock = null;

        // Remove all drop indicators
        this.removeDropIndicators();
      });
    }

    // Handle align button click
    const alignBtn = block.querySelector('.align-block');
    if (alignBtn) {
      alignBtn.addEventListener('click', () => {
        // Cycle through alignments: left -> center -> right -> left
        const currentAlign = block.style.textAlign || 'left';
        let newAlign;

        if (currentAlign === 'left') newAlign = 'center';
        else if (currentAlign === 'center') newAlign = 'right';
        else newAlign = 'left';

        this.applyBlockAlignment(block, newAlign);
      });
    }

    // Handle delete button click
    const deleteBtn = block.querySelector('.delete-block');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => {
        // Remove associated link preview if it exists
        if (block.id) {
          const externalPreview = document.querySelector(`.external-link-preview[data-for="${block.id}"]`);
          if (externalPreview) {
            externalPreview.remove();
          }
        }

        // Remove the block itself
        block.remove();
      });
    }

    // Add link preview functionality to editable content
    const editable = block.querySelector('.editable');
    if (editable && block.dataset.type === 'paragraph') {
      // Create an external link preview container for this block
      this.createExternalLinkPreview(block.id);

      // Add input event listener with debouncing
      let debounceTimer;

      editable.addEventListener('input', () => {
        console.log('Input event triggered');
        const currentText = editable.textContent;

        // Clear previous timer
        clearTimeout(debounceTimer);

        // Set a new timer to delay link preview checks
        debounceTimer = setTimeout(() => {
          if (typeof this.extractUrl !== 'function') {
            console.error('extractUrl method is not defined');
            return;
          }

          const urls = this.extractUrl(currentText);
          console.log('Extracted URLs:', urls);

          // Update the external preview
          this.updateExternalLinkPreview(block.id, urls);
        }, 1000);
      });
    }
  }

  // Add this helper method to your class
  showDropIndicators() {
    const mainContent = document.querySelector('.page-content') || document.body;
    mainContent.classList.add('dragging-active');
  }

  // Add these CSS styles to the addStyles method
  addStyles() {
    const styles = `
    .content-editable-area {
      min-height: 300px;
      position: relative;
      padding: 20px;
    }
    
    .editable-block {
      position: relative;
      margin: 10px 0;
      padding: 10px;
      border: 1px solid #e1e1e1;
      border-radius: 5px;
      background: #fff;
      transition: all 0.2s ease;
    }
    
    .editable-block:hover {
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
    }
    
    .block-controls1 {
      position: absolute;
      top: -30px;
      right: 0;
      background: #f5f5f5;
      border-radius: 3px;
      padding: 3px;
      opacity: 0;
      transition: opacity 0.2s;
      display: flex;
      z-index: 10;
    }
    
    .editable-block:hover .block-controls1 {
      opacity: 1;
    }
    
    .block-controls1 button {
      background: none;
      border: none;
      font-size: 16px;
      cursor: pointer;
      width: 28px;
      height: 28px;
      border-radius: 3px;
    }
    
    .block-controls1 button:hover {
      background: #e0e0e0;
    }
    
    .editable {
      outline: none;
      min-height: 1em;
    }
    
    .editable:focus {
      outline: none;
      border-bottom: 2px solid #007bff;
    }
    
    .editable:empty:before {
      content: attr(placeholder);
      color: #aaa;
    }
    
    .image-upload-wrapper, .featured-image-wrapper {
      width: 100%;
      position: relative;
      cursor: pointer;
    }
    
    .image-placeholder, .featured-image {
      width: 100%;
      height: auto;
      min-height: 200px;
      background: #f1f1f1;
      display: block;
      object-fit: contain;
    }
    
    .image-input {
      display: none;
    }
    
    .content-drop-zone {
      border: 2px dashed #ccc;
      padding: 20px;
      margin: 10px 0;
      text-align: center;
      background: #f9f9f9;
      transition: all 0.3s;
    }
    
    .content-drop-zone.hidden {
      display: none;
    }
    
    .content-drop-zone.drag-active {
      background: #e6f7ff;
      border-color: #1890ff;
    }
    
    .accept-drop {
      background: rgba(24, 144, 255, 0.05);
    }
    
    .dragging {
      opacity: 0.5;
      border: 1px dashed #007bff;
    }
    
    .drag-target {
      outline: 2px solid #007bff;
    }
    
    .drop-before {
      border-top: 3px solid #007bff;
    }
    
    .drop-after {
      border-bottom: 3px solid #007bff;
    }
    
    .external-link-preview {
      margin: 10px 0;
      border: 1px solid #e1e1e1;
      border-radius: 5px;
      overflow: hidden;
    }
    
    .previews-scroll-container {
      position: relative;
    }
    
    .previews-header {
      display: flex;
      justify-content: space-between;
      padding: 10px;
      background: #f5f5f5;
      border-bottom: 1px solid #e1e1e1;
    }
    
    .previews-controls button {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 16px;
      width: 30px;
      height: 30px;
      border-radius: 50%;
    }
    
    .previews-controls button:hover {
      background: #e0e0e0;
    }
    
    .previews-controls button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .previews-track {
      display: flex;
      overflow-x: auto;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none; /* Firefox */
    }
    
    .previews-track::-webkit-scrollbar {
      display: none; /* Chrome, Safari */
    }
    
    .preview-card {
      flex: 0 0 300px;
      margin: 10px;
      border-radius: 5px;
      overflow: hidden;
      border: 1px solid #e1e1e1;
      background: #fff;
    }
    

    .preview-content {
      padding: 15px;
    }
    
    .preview-content h3 {
      margin-top: 0;
      margin-bottom: 10px;
    }
    
    .preview-link {
      display: inline-block;
      margin-top: 10px;
      color: #007bff;
      text-decoration: none;
    }
    
    .preview-link:hover {
      text-decoration: underline;
    }
    
    .drag-over {
      border: 2px dashed #007bff;
      background-color: rgba(0, 123, 255, 0.1);
    }
    
    /* Additional styles for feature image block */
    .featured-image-block {
      width: 100%;
      margin-bottom: 30px;
    }
    
    .featured-image {
      width: 100%;
      max-height: 400px;
      object-fit: cover;
    }
  `;

    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
  }

  // Add this helper method to your class
  showDropIndicators() {
    const mainContent = document.querySelector('.page-content') || document.body;
    mainContent.classList.add('dragging-active');
  }

  // Add these CSS styles to the addStyles method
  addStyles() {
    const styles = `
    .content-editable-area {
      min-height: 300px;
      position: relative;
      padding: 20px;
    }
    
    .editable-block {
      position: relative;
      margin: 10px 0;
      padding: 10px;
      border: 1px solid #e1e1e1;
      border-radius: 5px;
      background: #fff;
      transition: all 0.2s ease;
    }
    
    .editable-block:hover {
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
    }
    
    .block-controls1 {
      position: absolute;
      top: -30px;
      right: 0;
      background: #f5f5f5;
      border-radius: 3px;
      padding: 3px;
      opacity: 0;
      transition: opacity 0.2s;
      display: flex;
      z-index: 10;
    }
    
    .editable-block:hover .block-controls1 {
      opacity: 1;
    }
    
    .block-controls1 button {
      background: none;
      border: none;
      font-size: 16px;
      cursor: pointer;
      width: 28px;
      height: 28px;
      border-radius: 3px;
    }
    
    .block-controls1 button:hover {
      background: #e0e0e0;
    }
    
    .editable {
      outline: none;
      min-height: 1em;
    }
    
    .editable:focus {
      outline: none;
      border-bottom: 2px solid #007bff;
    }
    
    .editable:empty:before {
      content: attr(placeholder);
      color: #aaa;
    }
    
    .image-upload-wrapper, .featured-image-wrapper {
      width: 100%;
      position: relative;
      cursor: pointer;
    }
    
    .image-placeholder, .featured-image {
      width: 100%;
      height: auto;
      min-height: 200px;
      background: #f1f1f1;
      display: block;
      object-fit: contain;
    }
    
    .image-input {
      display: none;
    }
    
    .content-drop-zone {
      border: 2px dashed #ccc;
      padding: 20px;
      margin: 10px 0;
      text-align: center;
      background: #f9f9f9;
      transition: all 0.3s;
    }
    
    .content-drop-zone.hidden {
      display: none;
    }
    
    .content-drop-zone.drag-active {
      background: #e6f7ff;
      border-color: #1890ff;
    }
    
    .accept-drop {
      background: rgba(24, 144, 255, 0.05);
    }
    
    .dragging {
      opacity: 0.5;
      border: 1px dashed #007bff;
    }
    
    .drag-target {
      outline: 2px solid #007bff;
    }
    
    .drop-before {
      border-top: 3px solid #007bff;
    }
    
    .drop-after {
      border-bottom: 3px solid #007bff;
    }
    
    .external-link-preview {
      margin: 10px 0;
      border: 1px solid #e1e1e1;
      border-radius: 5px;
      overflow: hidden;
    }
    
    .previews-scroll-container {
      position: relative;
    }
    
    .previews-header {
      display: flex;
      justify-content: space-between;
      padding: 10px;
      background: #f5f5f5;
      border-bottom: 1px solid #e1e1e1;
    }
    
    .previews-controls button {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 16px;
      width: 30px;
      height: 30px;
      border-radius: 50%;
    }
    
    .previews-controls button:hover {
      background: #e0e0e0;
    }
    
    .previews-controls button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .previews-track {
      display: flex;
      overflow-x: auto;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none; /* Firefox */
    }
    
    .previews-track::-webkit-scrollbar {
      display: none; /* Chrome, Safari */
    }
    
    .preview-card {
      flex: 0 0 300px;
      margin: 10px;
      border-radius: 5px;
      overflow: hidden;
      border: 1px solid #e1e1e1;
      background: #fff;
    }
    
 
    
    .preview-content {
      padding: 15px;
    }
    
    .preview-content h3 {
      margin-top: 0;
      margin-bottom: 10px;
    }
    
    .preview-link {
      display: inline-block;
      margin-top: 10px;
      color: #007bff;
      text-decoration: none;
    }
    
    .preview-link:hover {
      text-decoration: underline;
    }
    
    .drag-over {
      border: 2px dashed #007bff;
      background-color: rgba(0, 123, 255, 0.1);
    }
    
    /* Additional styles for feature image block */
    .featured-image-block {
      width: 100%;
      margin-bottom: 30px;
    }
    
    .featured-image {
      width: 100%;
      max-height: 400px;
      object-fit: cover;
    }
  `;

    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
  }


  createDropZone() {
    // Create drop zone
    this.dropZone = document.createElement('div');
    this.dropZone.className = 'content-drop-zone hidden';
    this.dropZone.innerHTML = `
    <div class="drop-indicator">
      <span>Drop content block here</span>
    </div>
  `;

    // Set up event listeners
    this.dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropZone.classList.add('drag-active');
    });

    this.dropZone.addEventListener('dragleave', () => {
      this.dropZone.classList.remove('drag-active');
    });

    this.dropZone.addEventListener('drop', (e) => {
      e.preventDefault();

      // Handle files
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        if (file.type.startsWith('image/')) {
          this.createImageBlock(file);
          this.hideDropZone();
          return;
        }
      }

      // Handle block types
      const blockType = e.dataTransfer.getData('text/plain');
      if (blockType) {
        this.createEditableBlock(blockType);
      }
      this.moveDropZoneToEnd();
      this.hideDropZone();
    });

    // Insert drop zone after last block, or at end if none
    const mainContent = document.querySelector('.page-content') || document.body;
    const lastBlock = mainContent.querySelector('.editable-block:last-of-type');

    if (lastBlock) {
      lastBlock.insertAdjacentElement('afterend', this.dropZone);
    } else {
      mainContent.appendChild(this.dropZone);
    }
  }

  moveDropZoneToEnd() {
    const mainContent = document.querySelector('.page-content') || document.body;
    const lastBlock = mainContent.querySelector('.editable-block:last-of-type');

    if (lastBlock) {
      lastBlock.insertAdjacentElement('afterend', this.dropZone);
    } else {
      mainContent.appendChild(this.dropZone);
    }
  }

  setupSidebarDrag() {
    const blocks = document.querySelectorAll('.content-block');

    blocks.forEach(block => {
      block.addEventListener('dragstart', (e) => {
        e.dataTransfer.setData('text/plain', block.dataset.type);
        block.classList.add('dragging');
        // Hide the sidebar immediately when dragging starts
        this.sidebar.hide();
        this.showDropZone();
      });

      block.addEventListener('dragend', (e) => {
        block.classList.remove('dragging');
        // Small delay to ensure drop event processes first
        setTimeout(() => this.hideDropZone(), 50);
      });
    });
  }

  showDropZone() {
    const mainContent = document.querySelector('.page-content') || document.body;
    if (!this.dropZone.parentElement) {
      mainContent.appendChild(this.dropZone);
    }
    this.dropZone.classList.remove('hidden');
  }

  hideDropZone() {
    this.dropZone.classList.add('hidden');
  }

  applyBlockAlignment(block, align) {
    if (!block) return;

    block.style.textAlign = align;

    if (align === 'center') {
      block.style.marginLeft = 'auto';
      block.style.marginRight = 'auto';
    } else if (align === 'right') {
      block.style.marginLeft = 'auto';
      block.style.marginRight = '0';
    } else {
      // align left
      block.style.marginLeft = '0';
      block.style.marginRight = 'auto';
    }
  }

  addStyles() {
    const styles = `
}
  `;

    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
  }


  getContentData() {
    const blocks = document.querySelectorAll('.editable-block');
    return Array.from(blocks).map(block => ({
      type: block.dataset.type,
      content: block.querySelector('.editable').textContent
    }));
  }

  removeDropIndicators() {
    document.querySelectorAll('.editable-block').forEach(block => {
      block.classList.remove('drop-before', 'drop-after', 'drag-target');
    });
  }
}